import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import AdminUsersManager from './AdminUsersManager';
import AdminEventsManager from './AdminEventsManager';
import AdminContentManager from './AdminContentManager';
import './AdminDashboard.css';

interface DashboardStats {
  overview: {
    totalUsers: number;
    totalCharacters: number;
    totalBattles: number;
    activeBattles: number;
    activeGathering: number;
    activeEvents: number;
  };
  recentActivity: {
    newUsersLast24h: number;
    newCharactersLast24h: number;
    battlesLast24h: number;
  };
  contentStats: {
    totalMaterials: number;
    totalRecipes: number;
    totalCraftedItems: number;
    totalGatheringActivities: number;
    totalEvents: number;
  };
  userRoles: Array<{
    role: string;
    count: number;
  }>;
  serverInfo: {
    serverTime: string;
    version: string;
    adminUser: string;
    adminRole: string;
  };
}

interface SystemHealth {
  status: string;
  timestamp: string;
  database: {
    healthy: boolean;
    responseTimeMs: number;
  };
  server: {
    uptime: number;
    memoryUsage: number;
    environment: string;
  };
}

interface AdminAccess {
  hasAccess: boolean;
  userId: number;
  role: string;
  isAdmin: boolean;
  isModerator: boolean;
}

const AdminDashboard: React.FC = () => {
  const { handleAuthError } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'events' | 'content' | 'logs'>('dashboard');
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [adminAccess, setAdminAccess] = useState<AdminAccess | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedingAchievements, setSeedingAchievements] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);
  const [generatingHexWorld, setGeneratingHexWorld] = useState(false);
  const [hexWorldMessage, setHexWorldMessage] = useState<string | null>(null);
  const [clearingHexWorld, setClearingHexWorld] = useState(false);
  const [clearHexWorldMessage, setClearHexWorldMessage] = useState<string | null>(null);

  const verifyAdminAccess = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/admin/verify-access');
      
      if (response.ok) {
        const data = await response.json();
        setAdminAccess(data);
        
        if (!data.hasAccess) {
          setError('Access denied - Admin or Moderator role required');
          return false;
        }
        return true;
      } else {
        setError('Unable to verify admin access');
        return false;
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return false;
      }
      setError('Error verifying admin access');
      return false;
    }
  }, [handleAuthError]);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/admin/dashboard');
      
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      } else {
        setError('Failed to load dashboard statistics');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading dashboard data');
    }
  }, [handleAuthError]);

  const fetchSystemHealth = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/admin/health');
      
      if (response.ok) {
        const data = await response.json();
        setSystemHealth(data);
      } else {
        console.warn('Failed to load system health data');
      }
    } catch (err) {
      console.warn('Error loading system health:', err);
    }
  }, []);

  useEffect(() => {
    const initializeAdmin = async () => {
      setIsLoading(true);
      const hasAccess = await verifyAdminAccess();
      
      if (hasAccess) {
        await Promise.all([
          fetchDashboardStats(),
          fetchSystemHealth()
        ]);
      }
      
      setIsLoading(false);
    };

    initializeAdmin();
  }, [verifyAdminAccess, fetchDashboardStats, fetchSystemHealth]);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatMemoryUsage = (mb: number): string => {
    if (mb > 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  const handleSeedAchievements = async () => {
    setSeedingAchievements(true);
    setSeedMessage(null);
    try {
      const response = await makeAuthenticatedRequest('/Achievement/seed', {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setSeedMessage(data.message || 'Achievements seeded successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to seed achievements' }));
        setSeedMessage(`Error: ${errorData.error || 'Failed to seed achievements'}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setSeedMessage('Error seeding achievements. Please try again.');
    } finally {
      setSeedingAchievements(false);
    }
  };

  const handleGenerateHexWorld = async () => {
    setGeneratingHexWorld(true);
    setHexWorldMessage(null);
    try {
      const response = await makeAuthenticatedRequest('/worldmap/admin/generate-hex-world', {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        const successMessage = `${data.message || 'Hex world generated successfully!'}\nTiles: ${data.tiles || 0}, Resources: ${data.resourceNodes || 0}, Monsters: ${data.monsterSpawns || 0}, Gates: ${data.gates || 0}`;
        setHexWorldMessage(successMessage);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate hex world' }));
        setHexWorldMessage(`Error: ${errorData.message || errorData.error || 'Failed to generate hex world'}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setHexWorldMessage(`Error generating hex world: ${apiError.message}`);
    } finally {
      setGeneratingHexWorld(false);
    }
  };

  const handleClearHexWorld = async () => {
    if (!window.confirm('Are you sure you want to clear the entire hex world? This cannot be undone!')) {
      return;
    }

    setClearingHexWorld(true);
    setClearHexWorldMessage(null);
    try {
      const response = await makeAuthenticatedRequest('/worldmap/admin/clear-hex-world', {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        setClearHexWorldMessage(data.message || 'Hex world cleared successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to clear hex world' }));
        setClearHexWorldMessage(`Error: ${errorData.error || 'Failed to clear hex world'}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setClearHexWorldMessage('Error clearing hex world. Please try again.');
    } finally {
      setClearingHexWorld(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !adminAccess?.hasAccess) {
    return (
      <div className="admin-dashboard">
        <div className="admin-error">
          <h2>🚫 Access Denied</h2>
          <p>{error || 'You do not have permission to access the admin dashboard.'}</p>
          <p>Admin or Moderator role is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-title">
          <h1>🛠️ Admin Dashboard</h1>
          <div className="admin-user-info">
            <span className="admin-role">{adminAccess.role}</span>
            <span className="admin-username">{dashboardStats?.serverInfo.adminUser}</span>
          </div>
        </div>
        
        {systemHealth && (
          <div className={`system-health ${systemHealth.status.toLowerCase()}`}>
            <div className="health-status">
              <span className="health-indicator"></span>
              <span>System {systemHealth.status}</span>
            </div>
            <div className="health-details">
              <span>DB: {systemHealth.database.responseTimeMs}ms</span>
              <span>Uptime: {formatUptime(systemHealth.server.uptime)}</span>
              <span>Memory: {formatMemoryUsage(systemHealth.server.memoryUsage)}</span>
            </div>
          </div>
        )}
      </div>

      <nav className="admin-nav">
        <button 
          className={`nav-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`nav-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users
        </button>
        <button 
          className={`nav-button ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          🎪 Events
        </button>
        <button 
          className={`nav-button ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          📦 Content
        </button>
        {adminAccess.isAdmin && (
          <button 
            className={`nav-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Logs
          </button>
        )}
      </nav>

      <div className="admin-content">
        {activeTab === 'dashboard' && dashboardStats && (
          <div className="dashboard-overview">
            <div className="stats-grid">
              <div className="stat-card overview-card">
                <h3>📊 System Overview</h3>
                <div className="stat-items">
                  <div className="stat-item">
                    <span className="stat-label">Total Users</span>
                    <span className="stat-value">{dashboardStats.overview.totalUsers.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Characters</span>
                    <span className="stat-value">{dashboardStats.overview.totalCharacters.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Battles</span>
                    <span className="stat-value">{dashboardStats.overview.totalBattles.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Active Battles</span>
                    <span className="stat-value active">{dashboardStats.overview.activeBattles}</span>
                  </div>
                </div>
              </div>

              <div className="stat-card activity-card">
                <h3>⚡ Recent Activity (24h)</h3>
                <div className="stat-items">
                  <div className="stat-item">
                    <span className="stat-label">New Users</span>
                    <span className="stat-value">{dashboardStats.recentActivity.newUsersLast24h}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">New Characters</span>
                    <span className="stat-value">{dashboardStats.recentActivity.newCharactersLast24h}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Battles Started</span>
                    <span className="stat-value">{dashboardStats.recentActivity.battlesLast24h}</span>
                  </div>
                </div>
              </div>

              <div className="stat-card content-card">
                <h3>📦 Content Statistics</h3>
                <div className="stat-items">
                  <div className="stat-item">
                    <span className="stat-label">Materials</span>
                    <span className="stat-value">{dashboardStats.contentStats.totalMaterials}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Recipes</span>
                    <span className="stat-value">{dashboardStats.contentStats.totalRecipes}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Crafted Items</span>
                    <span className="stat-value">{dashboardStats.contentStats.totalCraftedItems.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Active Events</span>
                    <span className="stat-value active">{dashboardStats.overview.activeEvents}</span>
                  </div>
                </div>
              </div>

              <div className="stat-card roles-card">
                <h3>👤 User Roles</h3>
                <div className="roles-breakdown">
                  {dashboardStats.userRoles.map(role => (
                    <div key={role.role} className="role-item">
                      <span className={`role-label ${role.role.toLowerCase()}`}>{role.role}</span>
                      <span className="role-count">{role.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="server-info">
              <h3>🖥️ Server Information</h3>
              <div className="server-details">
                <div className="server-item">
                  <span>Version:</span>
                  <span>{dashboardStats.serverInfo.version}</span>
                </div>
                <div className="server-item">
                  <span>Environment:</span>
                  <span>{systemHealth?.server.environment || 'Unknown'}</span>
                </div>
                <div className="server-item">
                  <span>Server Time:</span>
                  <span>{new Date(dashboardStats.serverInfo.serverTime).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {adminAccess.isAdmin && (
              <div className="admin-actions">
                <h3>⚙️ Admin Actions</h3>
                <div className="action-buttons">
                  <button
                    className="admin-action-button"
                    onClick={handleSeedAchievements}
                    disabled={seedingAchievements}
                  >
                    {seedingAchievements ? '⏳ Seeding...' : '🏆 Seed Achievements'}
                  </button>
                  {seedMessage && (
                    <div className={`seed-message ${seedMessage.includes('Error') ? 'error' : 'success'}`}>
                      {seedMessage}
                    </div>
                  )}
                </div>
                <p className="action-description">
                  Initialize 25 predefined achievements for the game. This only needs to be done once.
                </p>

                <div className="action-buttons" style={{ marginTop: '20px' }}>
                  <button
                    className="admin-action-button"
                    onClick={handleGenerateHexWorld}
                    disabled={generatingHexWorld}
                    style={{ background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' }}
                  >
                    {generatingHexWorld ? '⏳ Generating...' : '⬡ Generate Hex World'}
                  </button>
                  <button
                    className="admin-action-button"
                    onClick={handleClearHexWorld}
                    disabled={clearingHexWorld}
                    style={{ background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)' }}
                  >
                    {clearingHexWorld ? '⏳ Clearing...' : '🗑️ Clear Hex World'}
                  </button>
                  {hexWorldMessage && (
                    <div className={`seed-message ${hexWorldMessage.includes('Error') ? 'error' : 'success'}`}>
                      {hexWorldMessage}
                    </div>
                  )}
                  {clearHexWorldMessage && (
                    <div className={`seed-message ${clearHexWorldMessage.includes('Error') ? 'error' : 'success'}`}>
                      {clearHexWorldMessage}
                    </div>
                  )}
                </div>
                <p className="action-description">
                  Generate the procedural hex world (50x50 grid with 6 zones) or clear all hex world data. Generation takes ~30-60 seconds.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <AdminUsersManager />
        )}

        {activeTab === 'events' && (
          <AdminEventsManager />
        )}

        {activeTab === 'content' && (
          <AdminContentManager />
        )}

        {activeTab === 'logs' && adminAccess.isAdmin && (
          <div className="admin-section">
            <h2>📋 System Logs</h2>
            <p>System logs and error monitoring interface will be implemented here.</p>
            <div className="placeholder-content">
              <div className="feature-preview">
                <h3>Coming Soon:</h3>
                <ul>
                  <li>View recent system logs with filtering</li>
                  <li>Error monitoring and alerts</li>
                  <li>User action audit logs</li>
                  <li>Performance monitoring</li>
                  <li>Database query analysis</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;